"use client";

import { Suspense, useState } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { RequestError, requestJson } from "@/lib/clientRequest";
import { APP_HOME } from "@/lib/appHome";

function SignupForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const inviteToken = searchParams.get("invite") ?? "";
  const [name, setName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const data = await requestJson("/api/auth/signup", {
        method: "POST",
        body: { name, businessName, email, password, inviteToken },
        fallback: "Could not create the account",
      });
      if (data?.businessId) {
        document.cookie = `caw_business=${encodeURIComponent(data.businessId)}; path=/; max-age=31536000`;
      }
      router.replace(APP_HOME);
      router.refresh();
    } catch (cause) {
      setError(cause instanceof RequestError ? cause.message : "Could not create the account");
    } finally {
      setSubmitting(false);
    }
  }

  const inputClass =
    "w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2.5 text-base sm:text-sm text-zinc-100 outline-none transition focus:border-violet-500/60";

  return (
    <div className="flex min-h-dvh items-center justify-center bg-zinc-950 px-4 py-12">
      <div className="w-full max-w-sm space-y-8">
        <div className="space-y-2 text-center">
          <div className="mx-auto flex size-12 items-center justify-center rounded-2xl bg-violet-600 text-lg font-bold text-white">
            W
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-100">
            Create your account
          </h1>
          <p className="text-sm text-zinc-400">
            {inviteToken
              ? "You're joining an existing business — just create your account."
              : "Your business gets its own workers, knowledge base, and reports."}
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <label htmlFor="name" className="block text-sm font-medium text-zinc-300">
              Your name
            </label>
            <input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputClass}
            />
          </div>
          {!inviteToken && (
            <div className="space-y-1.5">
              <label
                htmlFor="businessName"
                className="block text-sm font-medium text-zinc-300"
              >
                Business name
              </label>
              <input
                id="businessName"
                required
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                className={inputClass}
              />
            </div>
          )}
          <div className="space-y-1.5">
            <label htmlFor="email" className="block text-sm font-medium text-zinc-300">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
            />
          </div>
          <div className="space-y-1.5">
            <label
              htmlFor="password"
              className="block text-sm font-medium text-zinc-300"
            >
              Password (8+ characters)
            </label>
            <input
              id="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputClass}
            />
          </div>

          {error ? (
            <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
              {error}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-lg bg-violet-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? "Creating…" : "Create account"}
          </button>
        </form>

        <p className="text-center text-sm text-zinc-500">
          Already have an account?{" "}
          <Link
            href={
              inviteToken
                ? `/login?next=${encodeURIComponent(`/invite/${inviteToken}`)}`
                : "/login"
            }
            className="text-violet-300 hover:underline"
          >
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

export default function SignupPage() {
  return (
    <Suspense fallback={<div className="min-h-dvh bg-zinc-950" aria-hidden />}>
      <SignupForm />
    </Suspense>
  );
}
